import apiRetryService, { DEFAULT_RETRY_CONFIG } from './apiRetryService';
import { listeningEnv } from '../config/listeningEnv';
import { debugError, debugWarn } from '../utils/debugLog';

export type ListeningEventType = 'start' | 'heartbeat' | 'stop';

export interface ListeningEventPayload {
  sessionId: string;
  timestamp: string;
  showId?: string | null;
  showSlug?: string | null;
  showTitle?: string | null;
  presenterId?: string | null;
  presenterName?: string | null;
  trackId?: string | null;
  trackTitle?: string | null;
  clientType?: string;
  clientVersion?: string;
  streamType?: string | null;
  playbackPositionSeconds?: number | null;
  listenSeconds?: number | null;
  durationSeconds?: number | null;
  sequence?: number | null;
  metadata?: Record<string, unknown>;
}

export interface ListeningEventResult {
  ok: boolean;
  skipped?: boolean;
  status?: number;
  body?: unknown;
  error?: Error;
}

const CLIENT_TYPE = 'web';

const resolveClientVersion = (): string => {
  const candidates = [
    'REACT_APP_LISTENING_CLIENT_VERSION',
    'VITE_LISTENING_CLIENT_VERSION',
    'REACT_APP_SENTRY_RELEASE',
    'VITE_SENTRY_RELEASE',
    'SENTRY_RELEASE',
    'REACT_APP_VERSION',
    'VITE_APP_VERSION'
  ];

  for (const key of candidates) {
    const value = process.env[key];
    if (value && value.trim().length > 0) {
      return value.trim();
    }
  }

  return 'dev';
};

const clientVersion = resolveClientVersion();

const sanitizeNumber = (value: number | null | undefined): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return undefined;
  }

  if (numeric < 0) {
    return 0;
  }

  return Math.floor(numeric);
};

const buildEndpoint = (eventType: ListeningEventType): string => {
  if (!listeningEnv.baseUrl) {
    throw new Error('Listening telemetry base URL is not configured.');
  }

  return `${listeningEnv.baseUrl.replace(/\/+$/, '')}/listening/${eventType}`;
};

const buildRequestBody = (
  eventType: ListeningEventType,
  payload: ListeningEventPayload
): Record<string, unknown> => {
  return {
    sessionId: payload.sessionId,
    timestamp: payload.timestamp,
    environment: listeningEnv.environment,
    eventType,
    showId: payload.showId ?? undefined,
    showSlug: payload.showSlug ?? undefined,
    showTitle: payload.showTitle ?? undefined,
    presenterId: payload.presenterId ?? undefined,
    presenterName: payload.presenterName ?? undefined,
    trackId: payload.trackId ?? undefined,
    trackTitle: payload.trackTitle ?? undefined,
    clientType: payload.clientType ?? CLIENT_TYPE,
    clientVersion: payload.clientVersion ?? clientVersion,
    streamType: payload.streamType ?? undefined,
    playbackPositionSeconds: sanitizeNumber(payload.playbackPositionSeconds),
    listenSeconds: sanitizeNumber(payload.listenSeconds),
    durationSeconds: sanitizeNumber(payload.durationSeconds),
    sequence: sanitizeNumber(payload.sequence),
    metadata: payload.metadata ?? undefined
  };
};

const listeningRetryConfig = {
  ...DEFAULT_RETRY_CONFIG,
  maxAttempts: 4,
  baseDelay: 500,
  maxDelay: 6000
};

export const createListeningSessionId = (): string =>
  `listen_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export async function sendListeningEvent(
  eventType: ListeningEventType,
  payload: ListeningEventPayload
): Promise<ListeningEventResult> {
  if (!listeningEnv.enableTelemetry) {
    debugWarn?.('Listening telemetry disabled; skipping event dispatch.', { eventType });
    return { ok: false, skipped: true };
  }

  if (!payload.sessionId || !payload.timestamp) {
    const error = new Error('Listening event payload missing required fields.');
    debugError?.(error);
    return { ok: false, error };
  }

  try {
    const endpoint = buildEndpoint(eventType);
    const body = buildRequestBody(eventType, payload);

    const response = await apiRetryService.fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      },
      listeningRetryConfig
    );

    const responseBody = await response.json().catch(() => undefined);

    if (!response.ok) {
      const error = new Error(`Listening telemetry request failed with status ${response.status}`);
      debugError?.(error, { eventType, status: response.status, body: responseBody });
      return {
        ok: false,
        status: response.status,
        body: responseBody,
        error
      };
    }

    return {
      ok: true,
      status: response.status,
      body: responseBody
    };
  } catch (error) {
    const wrappedError = error instanceof Error ? error : new Error(String(error));
    debugError?.('Listening telemetry dispatch failed', wrappedError);
    return {
      ok: false,
      error: wrappedError
    };
  }
}

const listeningMetricsService = Object.freeze({
  createSessionId: createListeningSessionId,
  sendEvent: sendListeningEvent
});

export default listeningMetricsService;
